import React from 'react';
import {
  Container,
  Typography,
  Box,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import SEOHead from '../components/SEOHead';

function TerminosYCondiciones() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <SEOHead
        title="Términos y Condiciones - VoyBus"
        description="Términos y condiciones de uso de los servicios de VoyBus. Lee nuestras políticas antes de realizar tu reserva."
        keywords={['términos y condiciones', 'políticas', 'VoyBus']}
        url="/terminos-y-condiciones"
        type="website"
        noindex={true}
      />
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa', py: 4 }} className="print-content">
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2.5rem' },
            mb: 2,
            color: 'text.primary',
          }}
        >
          🌎 Términos y Condiciones de VOYBUS.COM
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Última actualización: {new Date().toLocaleDateString('es-UY')}
          </Typography>

          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            ¡Bienvenido a VOYBUS.COM!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            Gracias por visitarnos y confiar en nosotros para acompañarte en tu próxima aventura. 
            Nuestra idea es que tengas una experiencia clara, segura y sin sorpresas. Por eso, 
            te compartimos estos Términos y Condiciones en un lenguaje simple y transparente.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontStyle: 'italic' }}>
            Al usar nuestro sitio o contratar un servicio, entendemos que aceptás lo que acá te contamos.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🧾 Quiénes Somos
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Nombre Fantasía:</strong> VOYBUS.COM
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Razón Social:</strong> AMERICA EN BUS S.R.L.
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>RUT:</strong> 21.615.812/0018
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Dirección:</strong> Rafael Pérez del Puerto 1037, Maldonado, Uruguay
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>MT:</strong> 228
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Email:</strong> hola@voybus.com
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Teléfono:</strong> +598 98 88 51 51
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🚌 ¿Qué Hacemos?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            En VOYBUS.COM ofrecemos y vendemos paquetes y servicios turísticos, con foco en 
            viajes en bus. Podés encontrar:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              ✅ Viajes operados directamente por nosotros
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              🤝 Viajes operados por empresas externas
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              🎒 Paquetes que combinan servicios de distintos proveedores (transporte, 
              alojamiento, excursiones, entradas, guías, etc.)
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Siempre te informaremos qué empresa opera el servicio que estás contratando.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🔎 Nuestro Rol
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Según el servicio que elijas, podemos actuar como:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              <strong>1. Operadores</strong>
              <br />
              Cuando AMERICA EN BUS S.R.L. organiza y ejecuta el viaje.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              <strong>2. Comercializadores o Intermediarios</strong>
              <br />
              Cuando te ayudamos a reservar un servicio que brinda otra empresa.
              En esos casos, somos el nexo entre el pasajero y el proveedor final, pero 
              no somos responsables por la prestación del servicio que no operamos.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              <strong>3. Cobradores "por cuenta y orden" de un tercero</strong>
              <br />
              A veces, VOYBUS puede encargarse únicamente del cobro.
              En ese caso, la compra se considera realizada directamente con el proveedor, 
              y te avisaremos antes de pagar.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            💳 Reservas y Pagos
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            <strong>2.1 Confirmación de Reserva</strong>
            <br />
            Todas las reservas están sujetas a disponibilidad y a la confirmación del proveedor.
            Una vez confirmada, recibirás un comprobante por correo electrónico con los 
            detalles de tu reserva.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            <strong>2.2 Formas de Pago</strong>
            <br />
            Aceptamos diversos medios de pago, como tarjetas de crédito y débito, y otros 
            métodos disponibles al momento de la compra.
            Los precios se muestran en dólares estadounidenses (USD), salvo que indiquemos 
            otra moneda.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            <strong>2.3 Precios</strong>
            <br />
            Los precios pueden variar hasta que la reserva esté confirmada y paga.
            Una vez realizado el pago y emitida la confirmación, el precio queda fijo según 
            lo acordado.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            ❌ Cancelaciones y Reembolsos
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            <strong>3.1 Cancelaciones por el Cliente</strong>
            <br />
            Cada servicio tiene sus propias políticas de cancelación.
            Te recomendamos consultar las condiciones específicas antes de confirmar la reserva.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            <strong>3.2 Cancelaciones por VOYBUS</strong>
            <br />
            Si por razones ajenas a nuestro control debemos cancelar un servicio, te ofreceremos 
            alternativas como reprogramación o un reembolso total.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            <strong>3.3 Reembolsos</strong>
            <br />
            Cuando corresponda un reembolso, se procesará según las políticas aplicables del 
            operador y puede demorar entre 7 y 30 días hábiles, dependiendo del método de pago.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🧳 Responsabilidades (sin letra chica)
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Queremos que tengas la mejor experiencia posible. Aun así, hay cosas que es importante 
            que sepas:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Cuando el servicio es brindado por un proveedor externo, la responsabilidad por su 
            correcta prestación es exclusiva de ese operador. Nosotros te acompañaremos si surge 
            algún inconveniente, pero no podemos asumir responsabilidades por servicios que no operamos.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            En los viajes pueden existir situaciones excepcionales o imprevistas, fuera del control 
            de VOYBUS o del operador (como condiciones climáticas, decisiones de autoridades, fuerza 
            mayor u otros hechos inevitables).
            En esos casos se aplicarán las políticas del operador y/o lo que establezca la normativa vigente.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Si por motivos excepcionales es necesario modificar itinerarios, horarios, servicios o 
            condiciones del viaje, el operador podrá hacer los ajustes razonables para continuar el 
            servicio de forma segura y viable. Siempre te avisaremos lo antes posible.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontStyle: 'italic' }}>
            Nuestro compromiso: acompañarte, ayudarte y buscar la mejor solución posible.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🙋‍♀️ Responsabilidades del Viajero
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Para que todo salga bien, te pedimos:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Brindar información correcta, real y actualizada al momento de la reserva. Información 
            incorrecta puede generar costos adicionales o incluso la cancelación del servicio sin reembolso.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Contar con toda la documentación necesaria y vigente para viajar, según tu nacionalidad 
            y destino. Esto puede incluir:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2, mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Documento de identidad y/o pasaporte válido
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Visas o permisos necesarios
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Requisitos sanitarios o certificados de vacunas
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              • Documentación y autorizaciones para menores
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            La responsabilidad de contar con la documentación correcta es exclusivamente del pasajero.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Mantener un comportamiento adecuado y respetuoso durante el viaje.
            En casos de comportamiento inapropiado, el operador o VOYBUS pueden suspender la 
            prestación del servicio sin derecho a reembolso.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🛡️ Seguro de Viaje
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Te recomendamos contratar un seguro de viaje que cubra cancelaciones, cambios, 
            asistencia médica, pérdida de equipaje y otros imprevistos.
            VOYBUS no brinda cobertura de seguros ni se hace responsable por gastos derivados 
            de situaciones no cubiertas por tu seguro.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🔐 Privacidad y Datos Personales
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Usamos tus datos únicamente para gestionar tu reserva, contactarte y mejorar tu experiencia.
            Podemos compartirlos con proveedores cuando sea necesario para brindarte el servicio contratado.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🧠 Contenido del Sitio
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            El contenido de VOYBUS.COM (textos, imágenes, logos, diseño, etc.) es propiedad de 
            AMERICA EN BUS S.R.L. y no puede utilizarse sin permiso.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            📝 Actualizaciones
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Podemos actualizar estos Términos y Condiciones cuando sea necesario. La versión 
            vigente es la publicada en este sitio.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            ⚖️ Ley Aplicable
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Estos términos se rigen por las leyes de la República Oriental del Uruguay.
            Cualquier situación legal se resolverá ante los tribunales competentes de Maldonado.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mt: 4, p: 3, backgroundColor: '#fff', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Gracias por llegar hasta acá ❤️
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Lo más importante: queremos que disfrutes tu viaje y vivas una experiencia espectacular.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
            Si tenés dudas, escribinos a{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
              hola@voybus.com
            </Box>
            {' '}— ¡estamos para ayudarte!
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }} className="no-print">
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              Imprimir PDF
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
    </>
  );
}

export default TerminosYCondiciones;